import { getFiveYearGrowth } from '@lib/api-services/suburbProfileService'
import { useState, useEffect } from 'react'

/**
 * Custom hook to fetch five year growth data for a suburb with optional filters
 * @param {string} slug - Suburb slug
 * @param {string} type - Type filter ('house', 'unit', or '')
 * @param {string} bedrooms - Bedrooms filter ('Overall', '1', '2', '3', '4', '5+')
 * @param {Array} initialData - Initial five year growth data from server-side props
 * @returns {Object} { fiveYearGrowth, isLoading, error }
 */
function useFiveYearGrowth(
  slug,
  type = 'all',
  bedrooms = 'Overall',
  initialData = null
) {
  const [fiveYearGrowth, setFiveYearGrowth] = useState(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!slug) {
      setFiveYearGrowth(initialData)
      return
    }

    const hasFilters = type || bedrooms
    if (!hasFilters) {
      setFiveYearGrowth(initialData)
      return
    }

    const fetchFilteredData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const params = {}
        if (type) params.type = type
        if (bedrooms) params.bedrooms = bedrooms

        const response = await getFiveYearGrowth(slug, params)
        setFiveYearGrowth(response.five_year_growth)
      } catch (err) {
        console.error('Error fetching filtered five year growth data:', err)
        setError(err)
        if (initialData) {
          setFiveYearGrowth(initialData)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchFilteredData()
  }, [type, bedrooms, slug, initialData])

  useEffect(() => {
    const hasFilters = type || bedrooms
    if (initialData && !hasFilters) {
      setFiveYearGrowth(initialData)
    }
  }, [initialData, type, bedrooms])

  return { fiveYearGrowth, isLoading, error }
}

export default useFiveYearGrowth

